import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { LOGO_URL } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

export default function MemoPrint() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  useEffect(()=>{ api.get(`/memos/${id}`).then(({data})=>setM(data)); }, [id]);

  // Force the freight memo to print on a single A4 page.
  useEffect(() => {
    const s = document.createElement("style");
    s.setAttribute("data-memo-print", "1");
    s.textContent = `@media print {
      @page { size: A4 portrait; margin: 8mm; }
      body * { visibility: hidden; }
      .memo-print-area, .memo-print-area * { visibility: visible; }
      .memo-print-area { position: absolute; left: 0; top: 0; width: 100%; max-width: 100% !important; box-shadow: none !important; padding: 0 !important; }
    }`;
    document.head.appendChild(s);
    return () => { s.remove(); };
  }, []);

  if (!m) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-6" data-testid="memo-print-page">
      <div className="no-print flex items-center justify-between mb-4">
        <Link to="/portal/memos" className="text-slate-600 hover:text-slate-900 text-sm inline-flex items-center gap-2"><ArrowLeft size={16}/> Back</Link>
        <button onClick={()=>window.print()} className="btn-primary px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2" data-testid="print-btn"><Printer size={16}/> Print / Save PDF</button>
      </div>
      <div className="print-area memo-print-area mx-auto bg-white shadow-sm p-8" style={{maxWidth: 900, fontFamily: 'Manrope', color:'#111'}}>
        <div className="flex items-start justify-between border-b-2 border-[#d4a017] pb-4">
          <img src={LOGO_URL} alt="JJ" style={{width:80, height:80, objectFit:'contain'}}/>
          <div className="text-center flex-1">
            <div className="font-display text-3xl" style={{color:'#0b3a7a'}}>JAI JAGDAMBA LOGISTICS</div>
            <div className="text-sm">17/1, Lakshmi Narayan Mukherjee Lane, Room No.209, 2nd Floor, KOLKATA-700006</div>
            <div className="text-sm text-[#0b5cc4]">jjlogistics.india@gmail.com | +91 98319 97959</div>
          </div>
          <div className="w-20"/>
        </div>

        <div className="text-center mt-4 tracking-widest text-sm font-semibold text-[#d4a017]">FREIGHT MEMO</div>

        <div className="grid grid-cols-2 gap-6 mt-4 text-sm">
          <div className="border border-slate-300 rounded p-3">
            <div className="grid grid-cols-2 gap-1"><span className="text-slate-500">Memo No</span><span className="font-mono font-semibold">{m.memo_no}</span></div>
            <div className="grid grid-cols-2 gap-1"><span className="text-slate-500">Date</span><span>{m.date}</span></div>
            <div className="grid grid-cols-2 gap-1"><span className="text-slate-500">Challan No</span><span className="font-semibold">{m.challan_no || "—"}</span></div>
            <div className="grid grid-cols-2 gap-1"><span className="text-slate-500">From</span><span>{m.from_location}</span></div>
            <div className="grid grid-cols-2 gap-1"><span className="text-slate-500">To</span><span>{m.to_location}</span></div>
          </div>
          <div className="border border-slate-300 rounded p-3">
            <div className="grid grid-cols-2 gap-1"><span className="text-slate-500">Truck No</span><span className="font-semibold">{m.truck_no}</span></div>
            <div className="grid grid-cols-2 gap-1"><span className="text-slate-500">Owner</span><span>{m.owner_name}{m.owner_phone ? ` · ${m.owner_phone}` : ""}</span></div>
            <div className="grid grid-cols-2 gap-1"><span className="text-slate-500">Owner PAN</span><span className="font-semibold">{m.owner_pan || "—"}</span></div>
            <div className="grid grid-cols-2 gap-1"><span className="text-slate-500">Driver</span><span>{m.driver_name}{m.driver_phone ? ` · ${m.driver_phone}` : ""}</span></div>
            <div className="grid grid-cols-2 gap-1"><span className="text-slate-500">DL No</span><span>{m.driver_license}</span></div>
          </div>
        </div>

        {(m.lorry_provider_name || m.lorry_provider_address) && (
          <div className="mt-3 border border-slate-300 rounded p-3 text-sm" data-testid="memo-print-lorry-provider">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Lorry Provider</div>
            <div className="font-semibold">{m.lorry_provider_name}</div>
            <div className="text-slate-700">{m.lorry_provider_address}</div>
          </div>
        )}

        <table className="w-full mt-4 text-sm border border-slate-800">
          <thead className="bg-slate-100">
            <tr>
              <th className="border border-slate-800 px-2 py-1 text-left">CN No</th>
              <th className="border border-slate-800 px-2 py-1 text-left">From</th>
              <th className="border border-slate-800 px-2 py-1 text-left">To</th>
              <th className="border border-slate-800 px-2 py-1 text-right">Pkgs</th>
              <th className="border border-slate-800 px-2 py-1 text-right">Weight (Kg)</th>
              <th className="border border-slate-800 px-2 py-1 text-right">Freight (₹)</th>
            </tr>
          </thead>
          <tbody>
            {m.cns.map((c,i)=>(
              <tr key={i}>
                <td className="border border-slate-800 px-2 py-1">{c.cn_no}</td>
                <td className="border border-slate-800 px-2 py-1">{c.from_location}</td>
                <td className="border border-slate-800 px-2 py-1">{c.to_location}</td>
                <td className="border border-slate-800 px-2 py-1 text-right">{c.packages}</td>
                <td className="border border-slate-800 px-2 py-1 text-right">{c.weight}</td>
                <td className="border border-slate-800 px-2 py-1 text-right">{c.freight}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex items-stretch gap-4" data-testid="memo-print-remarks-calc-row">
          <div className="flex-1 text-sm border-2 border-slate-800 rounded flex flex-col" data-testid="memo-print-remarks-box">
            <div className="px-3 py-1.5 bg-slate-100 border-b border-slate-800 text-xs uppercase tracking-widest font-semibold text-slate-700">Remarks</div>
            <div className="px-3 py-2 whitespace-pre-line flex-1">{m.remarks || "—"}</div>
          </div>
          <div className="w-96 text-sm border-2 border-slate-800 rounded" data-testid="memo-print-calc-block">
            <div className="px-3 py-1.5 bg-slate-100 border-b border-slate-800 text-xs uppercase tracking-widest font-semibold text-slate-700">Freight Calculation</div>
            <div className="px-3">
              <div className="flex justify-between py-1 border-b border-slate-200"><span>Freight</span><span>₹ {m.freight ?? m.total_freight}</span></div>
              <div className="flex justify-between py-1 border-b border-slate-200"><span>Detention</span><span>₹ {m.detention || "0"}</span></div>
              <div className="flex justify-between py-1 border-b border-slate-200"><span>Charges 1</span><span>₹ {m.charge1 || "0"}</span></div>
              <div className="flex justify-between py-1 border-b border-slate-200"><span>Charges 2</span><span>₹ {m.charge2 || "0"}</span></div>
              <div className="flex justify-between py-1.5 border-b border-slate-800 font-bold"><span>TOTAL</span><span>₹ {m.total_amount || "0"}</span></div>
              <div className="flex justify-between py-1 border-b border-slate-200"><span>Less: Bank Advance</span><span>− ₹ {m.bank_advance || "0"}</span></div>
              <div className="flex justify-between py-1"><span>Less: Cash Advance</span><span>− ₹ {m.cash_advance || "0"}</span></div>
            </div>
            <div className="flex justify-between px-3 py-2 bg-[#0b3a7a] text-white font-bold text-base">
              <span>BALANCE PAYABLE</span><span>₹ {m.balance_payable || m.balance}</span>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-3 gap-6 text-sm">
          <div>Driver's Signature</div>
          <div className="text-center">Owner's Signature</div>
          <div className="text-right">For JAI JAGDAMBA LOGISTICS<br/><br/><br/><span className="text-xs">Authorised Signatory</span></div>
        </div>
      </div>
    </div>
  );
}
